import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import styled from '@emotion/styled';

import { useQuery } from '@apollo/client/react';

import {
  DoorToDoor__BundleEnum,
  GeocodeDocument,
  Lead__ServiceNeeds,
} from '@graphql/platform';

import { useStorageCheckoutContext } from '@root/components/checkout/context';
import { useThirdPartyEventParams } from '@root/components/checkout/hooks/use_third_party_event_params';
import { MovingRedirectDialog } from '@root/components/checkout/product_pages/moving_redirect_dialog';
import { Modal as CouponModal } from '@root/components/checkout/product_pages/subcomponents/coupon/modal';
import {
  CheckoutVersion,
  StorageCheckoutStep,
} from '@root/components/checkout/types';
import { WTProvider, useTrack } from '@root/initializers/wt';
import { Commitment as CommitmentType } from '@root/resources/types/commitment';
import { PlanName } from '@root/resources/types/plan_name';
import { ServiceEnum } from '@root/resources/types/service';
import { EventSchema } from '@root/resources/wt/types';
import { WWW_ROUTES } from '@root/root/routes';

import { useClientDataContext } from '@shared/client_data_context';
import { useCreateLead } from '@shared/lead_form';
import { ServiceSelection } from '@shared/service_selector/constants';

import { useFlowInitiatedFunnelEvents } from '@utils/hooks/funnel_events/use_flow_initiated_funnel_event';
import { useTrackFunnelEvents } from '@utils/hooks/funnel_events/use_track_funnel_event';
import { reportError } from '@utils/monitoring';
import { ProtectionPlanEnum } from '@utils/protection_plan';
import {
  EventName,
  ProductType,
  createThirdPartyConversionEvent,
} from '@utils/third_party_conversion_events';

import { BookingSummary } from './booking_summary';
import {
  ChallengerFormSection,
  hasLeadToken,
  isChallengerBundleStepComplete,
  isChallengerDisposalStepComplete,
} from './completion';
import { FormContinue } from './form_continue';
import { useChallengerFlowStepFlags } from './hooks/use_challenger_flow_step_flags';
import { useChallengerPlanPricingPrefetch } from './hooks/use_challenger_plan_pricing_cache';
import { useChallengerSectionGating } from './hooks/use_challenger_section_gating';
import { useChallengerTaxRates } from './hooks/use_challenger_tax_rates';
import { ChallengerLayout } from './layout';
import { LearnMoreModal } from './learn_more_modal';
import { MobileStickyBar } from './mobile_sticky_bar';
import { ChallengerAppointmentCalendar } from './sections/appointment_calendar';
import { PackingSection } from './sections/packing';
import { PickupSection } from './sections/pickup';
import { PlanAndCommitmentSection } from './sections/plan_and_commitment';
import { ProtectionSection } from './sections/protection';
import { StorageLocationSection } from './sections/storage_location';
import {
  ContentColumn,
  GatedSection,
  MainRow,
  Page,
  SummaryColumn,
} from './shared';
import { CHALLENGER as T } from './tokens';

/** Figma mobile: location / plan / protection sit in one cluster with 12px gaps. */
const PlanCluster = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[12]};
  width: 100%;
`;

const FormFooter = styled.div`
  width: 100%;
  padding-top: ${T.space[8]};
`;

export const ChallengerStoragePlanPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const track = useTrack();
  const trackFunnelEvent = useTrackFunnelEvents();
  const thirdPartyEventParams = useThirdPartyEventParams('challenger');
  const [showOverlay, setShowOverlay] = useState(false);
  const [continuing, setContinuing] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [sliderDisplayPlanSize, setSliderDisplayPlanSize] = useState<
    PlanName | undefined
  >();
  const initiateFlowTriggeredRef = useRef(false);
  const createLead = useCreateLead();
  const wasPlanCompleteRef = useRef(false);
  const sectionRefs = useRef<
    Record<ChallengerFormSection, HTMLDivElement | null>
  >({
    storageLocation: null,
    plan: null,
    protection: null,
    packing: null,
    pickup: null,
  });

  const {
    data: {
      lead: { token },
    },
  } = useClientDataContext();

  const {
    disposalEligible,
    pricingSet,
    flowState: {
      getStepProps,
      onChange,
      goToStep,
      createStepTransition,
      values,
    },
    navigationState: { dispatch },
  } = useStorageCheckoutContext();

  const trimmedZip = values.zip?.trim() ?? '';

  const {
    data,
    loading: loadingGeocode,
    error: geocodeError,
  } = useQuery(GeocodeDocument, {
    variables: { zip: trimmedZip },
    skip: !trimmedZip,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const storageEligible = !!data?.geocode?.eligibleForDoorToDoor;
  const geocodeUnavailable = !!geocodeError && !data?.geocode;
  const geocode = data?.geocode;
  const taxRates = useChallengerTaxRates();

  const {
    firstIncompleteSection,
    isSectionIncomplete,
    planComplete,
    sectionEnabled,
  } = useChallengerSectionGating({
    storageEligible,
    loadingGeocode,
    geocodeUnavailable,
    leadToken: values.leadToken || token,
  });

  useFlowInitiatedFunnelEvents();
  useChallengerFlowStepFlags();
  useChallengerPlanPricingPrefetch(values.zip, pricingSet);

  useEffect(() => {
    if (storageEligible && !initiateFlowTriggeredRef.current) {
      initiateFlowTriggeredRef.current = true;
      createThirdPartyConversionEvent(EventName.INITIATE_FLOW, {
        zip_code: values.zip,
        checkout_version: CheckoutVersion.StorageV2,
        product_type: ProductType.SMART_STORAGE,
      });
    }
  }, [storageEligible, values.zip]);

  const scrollToSection = useCallback((section: ChallengerFormSection) => {
    sectionRefs.current[section]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, []);

  const handleValidationFailure = useCallback(() => {
    setShowValidationErrors(true);
    if (firstIncompleteSection) {
      scrollToSection(firstIncompleteSection);
    }
  }, [firstIncompleteSection, scrollToSection]);

  useEffect(() => {
    if (wasPlanCompleteRef.current && !planComplete) {
      handleValidationFailure();
    }
    wasPlanCompleteRef.current = planComplete;
  }, [handleValidationFailure, planComplete]);

  const setSectionRef = useCallback(
    (section: ChallengerFormSection) => (node: HTMLDivElement | null) => {
      sectionRefs.current[section] = node;
    },
    [],
  );

  const stepProps = {
    ...getStepProps(),
    enabled: true,
    scrollToStep: () => undefined,
    onChange: ((key, value, cb) => {
      onChange(key, value, (...args) => {
        cb?.(...args);
      });
    }) as typeof onChange,
  };

  const goToCart = () => {
    dispatch({ type: 'viewCart' });
    // Single-page layout never leaves in-funnel steps via the staircase, so
    // write `cart` explicitly (control gets this row when Reserve advances).
    void createStepTransition(
      StorageCheckoutStep.Cart,
      'next',
      {},
      values,
    ).catch((error: unknown) => {
      reportError(error instanceof Error ? error : new Error(String(error)), {
        tags: {
          component: 'ChallengerStoragePlanPage',
          action: 'createStepTransition',
        },
      });
    });
    history.push({
      pathname: WWW_ROUTES.STORAGE_CART,
      search: location.search,
    });
    goToStep(StorageCheckoutStep.Cart);
  };

  const handleContinue = async () => {
    if (!planComplete) {
      handleValidationFailure();
      return;
    }
    if (continuing) return;

    createThirdPartyConversionEvent(
      EventName.RESERVE_CLICKED,
      thirdPartyEventParams,
    );
    track({
      action: 'click',
      objectName: 'cart_cta',
      objectType: 'button',
      label: 'Continue',
    });

    setContinuing(true);
    let navigatedToCart = false;
    try {
      const hasContact =
        !!values.name?.trim() &&
        !!values.email?.trim() &&
        !!values.phone?.trim();
      if (
        hasContact &&
        !hasLeadToken({ values, leadToken: token }) &&
        !values.customerToken
      ) {
        const lead = await createLead({
          serviceContext: ServiceEnum.DoorToDoor,
          leadAttributes: {
            name: values.name,
            phone: values.phone,
            email: values.email,
            serviceNeeds: [Lead__ServiceNeeds.SmartStorage],
          },
          trackingParams: {
            zip_code: values.zip,
          },
        });
        onChange('leadToken', lead.token);
        if (lead.token && lead.id) {
          trackFunnelEvent({
            schema: EventSchema.WWW__LeadCaptureCompleted,
            action: 'submit',
            metadata: {
              name: values.name,
              email: values.email,
              lead_id: lead.id,
            },
          });
        }
      }
      goToCart();
      navigatedToCart = true;
    } catch (error) {
      reportError(error instanceof Error ? error : new Error(String(error)), {
        tags: { component: 'ChallengerStoragePlanPage' },
      });
      goToCart();
      navigatedToCart = true;
    } finally {
      if (!navigatedToCart) {
        setContinuing(false);
      }
    }
  };

  const continueDisabled = !planComplete || continuing;
  const bundleSelected = isChallengerBundleStepComplete(values);
  const disposalComplete = isChallengerDisposalStepComplete({
    values,
    disposalEligible,
  });
  const showSectionError = (section: ChallengerFormSection) =>
    showValidationErrors && isSectionIncomplete(section);

  return (
    <ChallengerLayout step="storage_plan">
      <MovingRedirectDialog />
      <Page>
        <MainRow>
          <ContentColumn>
            <WTProvider params={{ container: 'challenger_storage_plan' }}>
              <div ref={setSectionRef('storageLocation')}>
                <GatedSection $error={showSectionError('storageLocation')}>
                  <StorageLocationSection
                    onLearnMore={() => setShowOverlay(true)}
                  />
                </GatedSection>
              </div>

              <PlanCluster>
                <div ref={setSectionRef('plan')}>
                  <GatedSection
                    $disabled={!sectionEnabled.plan}
                    $error={showSectionError('plan')}
                  >
                    <PlanAndCommitmentSection
                      planSize={values.planSize}
                      commitment={values.commitment}
                      onPlanSizeChange={(plan: PlanName) =>
                        onChange('planSize', plan)
                      }
                      onCommitmentChange={(commitment: CommitmentType) =>
                        onChange('commitment', commitment)
                      }
                      onDisplayPlanSizeChange={setSliderDisplayPlanSize}
                    />
                  </GatedSection>
                </div>
              </PlanCluster>

              <GatedSection $disabled={!sectionEnabled.plan}>
                <ChallengerAppointmentCalendar
                  onChange={stepProps.onChange}
                  enabled={stepProps.enabled}
                  values={stepProps.values}
                />
              </GatedSection>

              <PlanCluster>
                <div ref={setSectionRef('protection')}>
                  <GatedSection
                    $disabled={!sectionEnabled.protection}
                    $error={showSectionError('protection')}
                  >
                    <ProtectionSection
                      value={values.protectionPlan}
                      onChange={(plan: ProtectionPlanEnum) =>
                        onChange('protectionPlan', plan)
                      }
                    />
                  </GatedSection>
                </div>
              </PlanCluster>

              <div ref={setSectionRef('packing')}>
                <GatedSection
                  $disabled={!sectionEnabled.packing}
                  $error={showSectionError('packing')}
                >
                  <PackingSection
                    value={values.bundleKind}
                    onChange={(bundle: DoorToDoor__BundleEnum) =>
                      onChange('bundleKind', bundle)
                    }
                    disposalSelected={values.disposalSelected}
                    onDisposalChange={(selected) =>
                      onChange('disposalSelected', selected)
                    }
                    disposalEnabled={bundleSelected}
                    showDisposalError={
                      showValidationErrors &&
                      bundleSelected &&
                      !disposalComplete
                    }
                  />
                </GatedSection>
              </div>

              <div ref={setSectionRef('pickup')}>
                <GatedSection
                  $disabled={!sectionEnabled.pickup}
                  $error={showSectionError('pickup')}
                >
                  <PickupSection
                    stepProps={stepProps}
                    showAddressValidationError={showSectionError('pickup')}
                  />
                </GatedSection>
              </div>

              <FormFooter>
                <FormContinue
                  disabled={continueDisabled}
                  loading={continuing}
                  onContinue={handleContinue}
                  showError={showValidationErrors && !planComplete}
                />
              </FormFooter>
            </WTProvider>
          </ContentColumn>

          <SummaryColumn>
            <BookingSummary taxRates={taxRates} />
          </SummaryColumn>
        </MainRow>
      </Page>

      <MobileStickyBar
        planComplete={planComplete}
        onContinue={handleContinue}
        displayPlanSize={sliderDisplayPlanSize}
        taxRates={taxRates}
        geocode={geocode}
        continueDisabled={continueDisabled}
      />

      <LearnMoreModal
        isOpen={showOverlay}
        onClose={() => setShowOverlay(false)}
      />
      <CouponModal serviceSelection={ServiceSelection.Storage} />
    </ChallengerLayout>
  );
};
